import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  X, Loader2, BarChart3, Trophy, AlertTriangle, Search, Plus, GitCompare,
  TrendingUp, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) => n == null ? "—" : new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(n);
const fmtPct = (n) => n == null ? "—" : `${Number(n).toFixed(1)}%`;

const COLOR_BY_INDEX = [
  { bg: "bg-violet-50",   border: "border-violet-300", text: "text-violet-900", chip: "bg-violet-100 text-violet-700" },
  { bg: "bg-emerald-50",  border: "border-emerald-300", text: "text-emerald-900", chip: "bg-emerald-100 text-emerald-700" },
  { bg: "bg-amber-50",    border: "border-amber-300", text: "text-amber-900", chip: "bg-amber-100 text-amber-700" },
  { bg: "bg-rose-50",     border: "border-rose-300", text: "text-rose-900", chip: "bg-rose-100 text-rose-700" },
  { bg: "bg-sky-50",      border: "border-sky-300", text: "text-sky-900", chip: "bg-sky-100 text-sky-700" },
  { bg: "bg-indigo-50",   border: "border-indigo-300", text: "text-indigo-900", chip: "bg-indigo-100 text-indigo-700" },
];

export default function MunicipiosCompareModal({ onClose }) {
  const [available, setAvailable] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/licitaciones-import/historico/municipios`);
        setAvailable(r.data?.municipios || []);
      } catch { toast.error("Error cargando municipios"); }
      finally { setLoadingList(false); }
    })();
  }, []);

  const filteredList = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return available.filter(m =>
      !selected.includes(m.municipio) &&
      (!f || m.municipio.toLowerCase().includes(f))
    );
  }, [available, filter, selected]);

  const addMuni = (name) => {
    if (selected.length >= 6) { toast.info("Máximo 6 municipios"); return; }
    if (!selected.includes(name)) setSelected(s => [...s, name]);
    setFilter("");
  };
  const removeMuni = (name) => setSelected(s => s.filter(x => x !== name));

  const runCompare = async () => {
    if (selected.length < 2) { toast.error("Selecciona al menos 2 municipios"); return; }
    setBusy(true);
    setCompareData(null);
    try {
      const munis = selected.join(",");
      const r = await axios.get(`${API}/licitaciones-import/historico/compare`, { params: { munis } });
      setCompareData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error comparando");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="munis-compare-modal"
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <GitCompare size={16} className="text-violet-600"/> Comparador de municipios
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Selecciona 2-6 municipios indexados y obtén una comparativa con win-rate, importe medio,
              ganadores dominantes, competidores compartidos y huecos de mercado.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Selector */}
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
            <p className="text-[11px] uppercase font-bold text-gray-500 mb-2">Municipios seleccionados ({selected.length}/6)</p>
            <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]">
              {selected.length === 0 && <span className="text-[11px] text-gray-400 italic">Ninguno aún. Búscalos abajo.</span>}
              {selected.map((name, i) => (
                <span
                  key={name}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-lg inline-flex items-center gap-1 ${COLOR_BY_INDEX[i % COLOR_BY_INDEX.length].chip}`}
                  data-testid={`compare-selected-${name}`}
                >
                  {name}
                  <button onClick={() => removeMuni(name)} className="hover:text-rose-600 ml-0.5"><X size={10}/></button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1 border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                <Search size={11} className="text-gray-400"/>
                <input
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="Buscar municipio…"
                  className="text-xs outline-none flex-1"
                  data-testid="compare-search"
                />
              </div>
              <button
                onClick={runCompare}
                disabled={busy || selected.length < 2}
                data-testid="compare-run"
                className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 size={12} className="animate-spin"/> : <BarChart3 size={12}/>}
                Comparar
              </button>
            </div>
            {/* Lista sugerencias */}
            {filter && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {loadingList ? (
                  <div className="p-3 text-center text-[11px] text-gray-400"><Loader2 className="inline animate-spin mr-1" size={11}/>Cargando…</div>
                ) : filteredList.length === 0 ? (
                  <div className="p-3 text-center text-[11px] text-gray-400">Sin coincidencias</div>
                ) : filteredList.slice(0, 10).map(m => (
                  <button
                    key={m.municipio_key}
                    onClick={() => addMuni(m.municipio)}
                    data-testid={`compare-add-${m.municipio_key}`}
                    className="w-full text-left px-3 py-1.5 hover:bg-violet-50 text-[11px] flex items-center justify-between border-b border-gray-50 last:border-0"
                  >
                    <span className="font-semibold text-gray-800 inline-flex items-center gap-1.5"><Plus size={10} className="text-violet-500"/>{m.municipio}</span>
                    <span className="text-gray-500">
                      {m.total} licit. · {fmtPct(m.win_rate)} win
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Resultados */}
          {compareData && (
            <div className="space-y-3" data-testid="compare-results">
              {compareData.count_found < compareData.count_requested && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-[11px] text-amber-800">
                  ⚠ {compareData.count_requested - compareData.count_found} municipio(s) sin índice (no aparecen en la comparativa).
                </div>
              )}

              {/* KPI cards side-by-side */}
              <div className={`grid gap-3 grid-cols-1 ${compareData.municipios.length === 2 ? "md:grid-cols-2" : compareData.municipios.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 xl:grid-cols-3"}`}>
                {compareData.municipios.map((m, idx) => {
                  const color = COLOR_BY_INDEX[idx % COLOR_BY_INDEX.length];
                  const isBestTotal = compareData.highlights?.total?.best_idx === idx;
                  const isBestWin = compareData.highlights?.win_rate?.best_idx === idx;
                  const isBestImp = compareData.highlights?.importe_medio?.best_idx === idx;
                  return (
                    <div key={m.requested} className={`rounded-xl border ${color.border} ${color.bg} p-3`} data-testid={`compare-card-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className={`text-sm font-bold ${color.text}`}>{m.municipio}</h4>
                        {!m.found && <span className="text-[10px] text-rose-700 font-semibold">Sin índice</span>}
                      </div>
                      {m.found ? (
                        <>
                          <div className="grid grid-cols-2 gap-2 text-[11px]">
                            <KPI label="Total" value={m.total} highlight={isBestTotal && "best"}/>
                            <KPI label="Win-rate" value={fmtPct(m.win_rate)} highlight={isBestWin && "best"}/>
                            <KPI label="Importe medio" value={m.importe_medio ? `${(m.importe_medio/1e6).toFixed(1)}M€` : "—"} highlight={isBestImp && "best"}/>
                            <KPI label="Ganador típico" value={m.ganador_tipico || "—"} icon={<Trophy size={10} className="text-amber-500"/>}/>
                          </div>
                          {m.competidores_rank?.length > 0 && (
                            <div className="mt-2 bg-white/70 rounded p-2">
                              <p className="text-[9px] uppercase font-bold text-gray-500 tracking-wide mb-1">Top competidores</p>
                              <div className="space-y-0.5">
                                {m.competidores_rank.slice(0, 3).map((c) => (
                                  <div key={c.name} className="flex items-center justify-between text-[10px]">
                                    <span className="text-gray-700">{c.name}</span>
                                    <span className="text-gray-500 font-mono">{c.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-[11px] text-gray-500 italic">No hay datos indexados para este municipio. Pulsa &ldquo;Aprender de mi histórico&rdquo; tras importar.</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Insights cruzados */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                    <TrendingUp size={12} className="text-emerald-600"/>
                    Competidores compartidos
                  </h4>
                  {compareData.competidores_compartidos?.length === 0 ? (
                    <p className="text-[11px] text-gray-400 italic">Ningún competidor coincide en todos los municipios seleccionados.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {compareData.competidores_compartidos.map(c => (
                        <span key={c} className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-semibold">{c}</span>
                      ))}
                    </div>
                  )}
                  {(compareData.competidores_unicos_por_municipio || []).some(arr => arr.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-[10px] uppercase font-bold text-gray-500 mb-1.5">Únicos por municipio</p>
                      <div className="space-y-1">
                        {compareData.municipios.map((m, i) => {
                          const u = (compareData.competidores_unicos_por_municipio || [])[i] || [];
                          if (u.length === 0) return null;
                          return (
                            <div key={m.requested} className="text-[10px] flex items-start gap-2">
                              <span className="font-semibold text-gray-700 w-20 shrink-0 truncate">{m.municipio}:</span>
                              <span className="text-gray-600">{u.join(", ")}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <h4 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                    <AlertTriangle size={12} className="text-rose-600"/>
                    Huecos de mercado
                  </h4>
                  {compareData.huecos_de_mercado?.length === 0 ? (
                    <p className="text-[11px] text-gray-400 italic">Valoriza es ganador típico en todos los municipios analizados (o sin datos suficientes).</p>
                  ) : (
                    <div className="space-y-1.5">
                      {compareData.huecos_de_mercado.map(h => (
                        <div key={h.municipio} className="bg-rose-50 border border-rose-200 rounded p-2">
                          <div className="text-[11px] font-bold text-rose-800">{h.municipio}</div>
                          <div className="text-[10px] text-rose-700">
                            Dominado por <b>{h.ganador_dominante}</b> · {h.total} licit. · ~{(h.importe_medio/1e6 || 0).toFixed(1)}M€
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!compareData && !busy && (
            <div className="text-center py-10 text-[11px] text-gray-400">
              Añade municipios y pulsa &ldquo;Comparar&rdquo; para ver el análisis.
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, highlight, icon }) {
  return (
    <div className={`rounded p-1.5 ${highlight === "best" ? "bg-emerald-100 border border-emerald-300" : "bg-white/70"}`}>
      <div className="text-[9px] uppercase text-gray-500 font-bold tracking-wide flex items-center gap-1">
        {icon}{label}
        {highlight === "best" && <span className="text-emerald-600 text-[8px]">🏆 best</span>}
      </div>
      <div className={`text-xs font-bold ${highlight === "best" ? "text-emerald-800" : "text-gray-900"} truncate`}>{value}</div>
    </div>
  );
}
